import {
  RAMADAN_2026,
  type CityKey,
  type CityRamadanTimings,
  type RamadanDayShort,
} from "@/domain/ramadan";
import { computed, onMounted, onUnmounted, ref } from "vue";

const PAKISTAN_TIME_ZONE = "Asia/Karachi";

/* -------------------------------------------------------
   NORMALIZED (LONG-FORM) DAY TYPE
------------------------------------------------------- */
export interface RamadanDay {
  gregorianDate: string;
  hijriDate: string;
  rozaNumber: number;
  ashra: 1 | 2 | 3 | 0;
  sehriEnds: string;
  iftarTime: string;
  isRamadan: boolean;
  isEid: boolean;
}

/* -------------------------------------------------------
   ONE-TIME NORMALIZATION FUNCTION
------------------------------------------------------- */
function normalizeDay(d: RamadanDayShort): RamadanDay {
  const hijriDate = d.h.trim();
  const isRamadan = /\bRamadan\b/i.test(hijriDate);
  const shawwalDayMatch = hijriDate.match(/^(\d+)\s+Shawwal\b/i);
  const shawwalDay = shawwalDayMatch
    ? Number.parseInt(shawwalDayMatch[1] ?? "", 10)
    : null;

  return {
    gregorianDate: d.g,
    hijriDate,
    rozaNumber: d.r,
    ashra: d.a,
    sehriEnds: d.s,
    iftarTime: d.i,
    isRamadan,
    isEid: shawwalDay === 1,
  };
}

function buildDayTimestamps(day: RamadanDay) {
  if (!day.isRamadan) return null;

  return {
    sehriEndsAt: new Date(`${day.gregorianDate}T${day.sehriEnds}:00+05:00`),
    iftarAt: new Date(`${day.gregorianDate}T${day.iftarTime}:00+05:00`),
  };
}

/* -------------------------------------------------------
   COMPOSABLE
------------------------------------------------------- */
export function useRamadanTime() {
  const now = ref(new Date());
  const cityKey = useState<CityKey>("ramadan-city-key", () => "islamabad");

  const cityData = computed<CityRamadanTimings>(() => {
    return RAMADAN_2026[cityKey.value];
  });

  /* Normalize ALL days once per city */
  const normalizedDays = computed<RamadanDay[]>(() =>
    cityData.value.days.map(normalizeDay),
  );

  const toTimeZoneDateString = (d: Date, timeZone: string) => {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);

    const y = parts.find((p) => p.type === "year")?.value ?? "";
    const m = parts.find((p) => p.type === "month")?.value ?? "";
    const day = parts.find((p) => p.type === "day")?.value ?? "";
    return `${y}-${m}-${day}`;
  };

  const todayString = computed(() =>
    toTimeZoneDateString(now.value, PAKISTAN_TIME_ZONE),
  );

  const today = computed<RamadanDay | null>(() => {
    return (
      normalizedDays.value.find((d) => d.gregorianDate === todayString.value) ||
      null
    );
  });

  const firstRamadanDay = computed(() => {
    return normalizedDays.value.find((d) => d.isRamadan) ?? null;
  });

  const lastRamadanDay = computed(() => {
    for (let i = normalizedDays.value.length - 1; i >= 0; i -= 1) {
      const day = normalizedDays.value[i];
      if (day?.isRamadan) return day;
    }
    return null;
  });

  const phase = computed(() => {
    if (!firstRamadanDay.value || !lastRamadanDay.value) return "AFTER_RAMADAN";

    const todayDate = todayString.value;
    const firstDate = firstRamadanDay.value.gregorianDate;
    const lastDate = lastRamadanDay.value.gregorianDate;

    if (todayDate < firstDate) return "BEFORE_RAMADAN";
    if (todayDate > lastDate) return "AFTER_RAMADAN";
    return "DURING_RAMADAN";
  });

  /* Days until Ramadan (for BEFORE_RAMADAN countdown) */
  const daysUntilRamadan = computed(() => {
    if (phase.value !== "BEFORE_RAMADAN" || !firstRamadanDay.value) return 0;

    const today = Date.parse(`${todayString.value}T00:00:00Z`);
    const ramadanStart = Date.parse(
      `${firstRamadanDay.value.gregorianDate}T00:00:00Z`,
    );

    const diff = Math.floor((ramadanStart - today) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  });

  const timestamps = computed(() => {
    if (!today.value) return null;
    return buildDayTimestamps(today.value);
  });

  const currentEvent = computed(() => {
    if (phase.value !== "DURING_RAMADAN") return "NONE";
    if (!today.value || !timestamps.value) return "NONE";

    if (now.value < timestamps.value.sehriEndsAt) return "SEHRI";
    if (now.value < timestamps.value.iftarAt) return "IFTAR";
    return "NONE";
  });

  const targetTime = computed<Date | null>(() => {
    if (phase.value === "AFTER_RAMADAN") return null;

    if (phase.value === "BEFORE_RAMADAN") return null;

    if (!timestamps.value) return null;

    if (currentEvent.value === "IFTAR") return timestamps.value.iftarAt;

    if (currentEvent.value === "SEHRI") return timestamps.value.sehriEndsAt;

    return null;
  });

  const countdownMs = computed(() => {
    if (!targetTime.value) return null;
    const ms = targetTime.value.getTime() - now.value.getTime();
    if (ms <= 1000) return null;
    return ms;
  });

  /* Remaining roza count (during Ramadan) */
  const remainingRoza = computed(() => {
    if (
      phase.value !== "DURING_RAMADAN" ||
      !today.value ||
      !lastRamadanDay.value
    )
      return 0;
    const totalDays = lastRamadanDay.value.rozaNumber;
    const current = today.value.rozaNumber;
    return Math.max(0, totalDays - current);
  });

  function formatCountdown(ms: number) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;

    return (
      h.toString().padStart(2, "0") +
      ":" +
      m.toString().padStart(2, "0") +
      ":" +
      s.toString().padStart(2, "0")
    );
  }

  const countdown = computed(() =>
    countdownMs.value === null ? "" : formatCountdown(countdownMs.value),
  );

  const showCountdown = computed(() => {
    // Show countdown during Ramadan (iftar/sehri)
    if (phase.value === "DURING_RAMADAN") {
      return countdownMs.value !== null;
    }
    // Show countdown before Ramadan if days remaining > 0
    if (phase.value === "BEFORE_RAMADAN") {
      return daysUntilRamadan.value > 0;
    }
    return false;
  });

  function setCity(newCity: CityKey) {
    cityKey.value = newCity;
  }

  let timer: number | null = null;

  onMounted(() => {
    timer = window.setInterval(() => {
      now.value = new Date();
    }, 1000);
  });

  onUnmounted(() => {
    if (timer) window.clearInterval(timer);
  });

  return {
    /* state */
    now,
    cityKey,
    cityData,

    /* normalized data */
    today,
    normalizedDays,

    /* time logic */
    currentEvent,
    phase,
    countdown,
    countdownMs,
    showCountdown,
    daysUntilRamadan,
    remainingRoza,

    /* actions */
    setCity,
  };
}
